import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { LINE_GRADIENT } from '../../constants/gradients';

interface ScreenshotCarouselProps {
  screenshots: string[];
  gameName: string;
  iconUrl?: string | null;
  coverImageUrl?: string | null;
}

export default function ScreenshotCarousel({ 
  screenshots, 
  gameName,
  iconUrl,
  coverImageUrl 
}: ScreenshotCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Background is cover or icon (blurred)
  // Clean light gray placeholder with centered Lucide Gamepad2 icon
  const gamepadPlaceholder = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"%3E%3Crect width="400" height="300" fill="%23f1f5f9"/%3E%3Cg transform="translate(200,150) scale(4.7) translate(-12,-12)"%3E%3Cline x1="6" y1="11" x2="10" y2="11" stroke="%2364748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/%3E%3Cline x1="8" y1="9" x2="8" y2="13" stroke="%2364748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/%3E%3Cline x1="15" y1="12" x2="15.01" y2="12" stroke="%2364748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/%3E%3Cline x1="18" y1="10" x2="18.01" y2="10" stroke="%2364748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/%3E%3Cpath d="M17.32 5H6.68a4 4 0 0 0-3.978 3.59c-.006.052-.01.101-.017.152C2.604 9.416 2 14.456 2 16a3 3 0 0 0 3 3c1 0 1.5-.5 2-1l1.414-1.414A2 2 0 0 1 9.828 16h4.344a2 2 0 0 1 1.414.586L17 18c.5.5 1 1 2 1a3 3 0 0 0 3-3c0-1.545-.604-6.584-.685-7.258-.007-.05-.011-.1-.017-.151A4 4 0 0 0 17.32 5z" fill="none" stroke="%2364748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/%3E%3C/g%3E%3C/svg%3E';
  const backgroundImage = coverImageUrl || iconUrl || gamepadPlaceholder;
  
  // Display images are only screenshots (or placeholder if none)
  const displayImages = screenshots.length > 0 ? screenshots : [gamepadPlaceholder];

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : displayImages.length - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev < displayImages.length - 1 ? prev + 1 : 0));
  };

  const goToIndex = (index: number) => {
    setCurrentIndex(index);
  };

  return (
    <div className="relative w-full bg-gray-100 dark:bg-gray-800 rounded-xl overflow-hidden group">
      {/* Phone aspect ratio container */}
      <div className="relative w-full" style={{ height: '500px' }}>
        {/* Blurred Background */}
        <div className="absolute inset-0">
          <img
            src={backgroundImage}
            alt={`${gameName} background`}
            className="w-full h-full object-cover blur-xl scale-110"
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>


        {/* Screenshots */}
        {displayImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
              index === currentIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={image}
              alt={`${gameName} - Screenshot ${index + 1}`}
              className="w-full h-full object-contain"
            />
          </div>
        ))}

        {/* Navigation Arrows */}
        {displayImages.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-white/20 hover:scale-110 z-10"
              aria-label="Previous"
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>

            <button
              onClick={goToNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-white/20 hover:scale-110 z-10"
              aria-label="Next"
            >
              <ChevronRight className="w-5 h-5 text-white" />
            </button>
          </>
        )}

        {/* Dots Indicator */}
        {displayImages.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {displayImages.map((_, index) => (
              <button
                key={index}
                onClick={() => goToIndex(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'w-8'
                    : 'w-2 hover:w-4'
                }`}
                style={{
                  background: index === currentIndex 
                    ? LINE_GRADIENT
                    : 'rgba(255, 255, 255, 0.4)'
                }}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* Image Counter */}
        {displayImages.length > 1 && (
          <div className="absolute top-4 right-4 px-3 py-1 bg-black/60 backdrop-blur-sm text-white text-sm font-medium rounded-full">
            {currentIndex + 1} / {displayImages.length}
          </div>
        )}
      </div>
    </div>
  );
}
